import React from "react";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { Accordion, Box, BoxReset, Text, ThemeProps, Title } from "common/components/ui";
import { Currency, CurrencyFormat } from "common/components/Currency";
import { useShipmentConfirmation } from "./useShipmentConfirmation";
import { ElevioArticleId, ElevioLearnMoreButton } from "common/elevio";
import { InfoIcon } from "common/components/icons";
import { ShipmentConfirmationEstimatedCostBreakup } from "./ShipmentConfirmationEstimatedCostBreakup";
import { useShipmentConfirmationCostTable } from "./useShipmentConfirmationCostTable";
import { ShipmentConfirmationTile } from "common/components/ShipmentConfirmationTile";
import { StorageFreightDeliveryInformation } from "./StorageFreightDeliveryInformation";
import { CommonPalletDiscrepancy } from "inbounds/steps/ship/view/cards/confirmation/sharedComponents/CommonPalletDiscrepancy";
import { useStorageAppointmentDetails } from "./useStorageAppointmentDetails";
import { useStorageShipmentDetail } from "../StorageShipmentDetail/useStorageShipmentDetail";
import { InboundShipmentStatus } from "@deliverr/inbound-client";

const ShipmentDetailContainer = styled.div<ThemeProps>(
  ({ theme }) => `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.S4};
  width: 100%;
  line-height: ${theme.spacing.S4};
`
);

const StyledTitle = styled(Title)<ThemeProps>(
  ({ theme }) => `
      line-height: ${theme.font.lineHeight.LH2};
      font-size: ${theme.font.size.F2};
    `
);

const BorderedContainer = styled.div<ThemeProps>(
  ({ theme }) => `
    border-top: ${theme.border.width.B1} ${theme.border.type} ${theme.colors.NEUTRAL[80]};
    border-bottom: ${theme.border.width.B1} ${theme.border.type} ${theme.colors.NEUTRAL[80]};
  `
);

const BorderedContainerWithoutBottom = styled.div<ThemeProps>(
  ({ theme }) => `
    border-top: ${theme.border.width.B1} ${theme.border.type} ${theme.colors.NEUTRAL[80]};
  `
);

export const StorageShipmentConfirmation: React.FC = () => {
  const title = (
    <BoxReset margin={null}>
      <StyledTitle displayAs="h5" as="h5" uppercase>
        <FormattedMessage id={"storage.inbounds.detail.confirmation.title"} defaultMessage={"SHIPMENT CONFIRMATION"} />
      </StyledTitle>
    </BoxReset>
  );

  const learnMore = <ElevioLearnMoreButton articleId={ElevioArticleId.ReserveStorage} />;

  const {
    cargoTypeTitle,
    shippingTitle,
    cargoTypeContent,
    shippingContent,
    shippingDetails,
    toggleOpen,
    isOpen,
    isZoneBasedMerchant,
    palletCount,
  } = useShipmentConfirmation();

  const { costTitle, cost, subTables } = useShipmentConfirmationCostTable();
  const { quantity, verifiedQuantity } = useStorageAppointmentDetails();

  const { status } = useStorageShipmentDetail();
  const shouldShowFreightDeliveryInfo =
    status !== InboundShipmentStatus.READY_FOR_WAREHOUSE &&
    status !== InboundShipmentStatus.WAREHOUSE_APPOINTMENT_NEEDED;

  const costDetails = <ShipmentConfirmationEstimatedCostBreakup subTables={subTables} />;
  const isPalletized = cargoTypeContent === "Pallets";
  const displayCargoType = isPalletized ? "Palletized" : cargoTypeContent;
  const expectedBoxes = null; // This should come from source

  const ContainerComponent = shouldShowFreightDeliveryInfo ? BorderedContainer : BorderedContainerWithoutBottom;

  return (
    <Accordion indicator isOpen={isOpen} toggleOpen={toggleOpen} headerContent={title} noBodyPadding>
      <ShipmentDetailContainer>
        <ShipmentConfirmationTile data-testid="cargo-type-title" header={cargoTypeTitle} content={displayCargoType} />
        {isPalletized ? (
          <CommonPalletDiscrepancy
            expectedPallets={quantity}
            actualPallets={verifiedQuantity}
            expectedBoxes={expectedBoxes}
            shipmentContentPallets={palletCount}
          />
        ) : (
          <ShipmentConfirmationTile
            data-testid="shipment-confirmation-shipping-title"
            header={shippingTitle}
            content={shippingContent}
            footer={shippingDetails}
          />
        )}
        {!isZoneBasedMerchant && (
          <ShipmentConfirmationTile
            data-testid="shipment-confirmation-cost-title"
            header={
              <Text size="bodyLarge">
                {costTitle}
                <Box as="span" marginLeft="S1">
                  <InfoIcon wrap messageWidth={300} placement="top" message={costDetails} />
                </Box>
              </Text>
            }
            content={<Currency value={cost} currency={CurrencyFormat.USD} />}
            footer={learnMore}
          />
        )}
      </ShipmentDetailContainer>
      {shouldShowFreightDeliveryInfo && (
        <ContainerComponent>
          <StorageFreightDeliveryInformation />
        </ContainerComponent>
      )}
    </Accordion>
  );
};
